import Title from "common/Typography/Title";
import { FILTERS_COLUMN_ID } from "testIds";
import Flex from "common/Boxes/Flex";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { PAGE_SPINNER_MARGIN_FROM_TOP } from "common/constants";
import { CSSProperties, ReactNode, useMemo, useRef } from "react";
import { useTheme } from "styled-components";
import FiltersSection from "common/Filters/FiltersColumn/FiltersSection";
import { FiltersGroupProps } from "@shared/features/filters/types";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import LinkButton from "common/Buttons/LinkButton";
import useFiltersQuery from "common/Filters/common/useFiltersQuery";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Card from "common/Boxes/Card";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { SlidersHorizontal, X } from "lucide-react";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";

interface FiltersColumnProps extends FiltersGroupProps {
  width?: number;
  height?: string;
  expanded?: boolean;
  style?: CSSProperties;
  prefixNode?: ReactNode;
  closeMobileFilters?: () => void;
}

const FiltersColumn = ({
  selectLoadingStates,
  filtersSelectors,
  filtersActions,
  hideFilterTypes,
  isLoading,
  filterTypeToTitle,
  width,
  height,
  expanded,
  style,
  prefixNode,
  closeMobileFilters,
}: FiltersColumnProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const filtersRef = useRef<HTMLDivElement>(null);
  const appTheme = useTheme();
  const dispatch = useDispatch();
  const filtersConfig = useSelector(filtersSelectors.selectFiltersConfig);
  const filtersData = useSelector(filtersSelectors.selectFiltersData);
  useFiltersQuery(filtersSelectors, filtersActions, hideFilterTypes);
  const defaultLoading = useMemo(
    () => ({
      loading: false,
    }),
    []
  );
  const { loading } = useSelector(
    selectLoadingStates ?? (() => defaultLoading)
  );

  const filterTypes = filtersConfig.filterTypes?.filter(
    (filterType) =>
      filterType in filtersData && !hideFilterTypes?.includes(filterType)
  );

  const scrollbarWidth =
    (filtersRef.current?.offsetWidth || 0) -
    (filtersRef.current?.clientWidth || 0);

  const filtersContent = (
    <LoadingSpinnerOverlay
      isLoading={isLoading || loading}
      spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
      backgroundColor={appTheme.colors.cardBgColor}
    >
      {prefixNode}
      {appTheme.isNewLook ? (
        <NewLookHeader>
          <NewLookTitle>{t("filters.common.title")}</NewLookTitle>
          <NewLookReset
            type="button"
            onClick={() => dispatch(filtersActions.selectAll(undefined))}
          >
            {t("filters.common.reset")}
          </NewLookReset>
          {!!closeMobileFilters && (
            <NewLookClose
              type="button"
              onClick={closeMobileFilters}
              aria-label="close"
            >
              <X size={18} strokeWidth={NEW_LOOK_ICON_STROKE} />
            </NewLookClose>
          )}
        </NewLookHeader>
      ) : (
        <FiltersWrapper>
          <Title type="secondary" level={3}>
            {t("filters.common.title")}
          </Title>
          <Flex>
            <LinkButton
              type="link"
              onClick={() => dispatch(filtersActions.selectAll(undefined))}
              style={{ padding: 0 }}
            >
              {t("filters.common.reset")}
            </LinkButton>
            {!!closeMobileFilters && (
              <CloseOutlined
                onClick={closeMobileFilters}
                style={{ marginLeft: 12 }}
              />
            )}
          </Flex>
        </FiltersWrapper>
      )}
      <Sections $flexDirection="column">
        {filterTypes?.map((filterType) => {
          return (
            <FiltersSection
              filterType={filterType}
              filtersSelectors={filtersSelectors}
              filtersActions={filtersActions}
              key={filterType}
              filterTypeToTitle={filterTypeToTitle}
            />
          );
        })}
      </Sections>
      {appTheme.isNewLook && !(isLoading || loading) && (
        // Shown by CSS only while no section rendered (sections with no
        // options render nothing).
        <NewLookEmpty>
          <NewLookEmptyIcon>
            <SlidersHorizontal size={16} strokeWidth={1.75} />
          </NewLookEmptyIcon>
          <NewLookEmptyTitle>
            {t("filters.common.empty_title")}
          </NewLookEmptyTitle>
          {t("filters.common.empty_description")}
        </NewLookEmpty>
      )}
    </LoadingSpinnerOverlay>
  );

  if (expanded !== undefined) {
    return (
      <ExpandCollapse expanded={expanded} horizontal={true} style={style}>
        <div style={{ width, height }}>
          <ExpandCollapseFiltersWrapper
            data-testid={FILTERS_COLUMN_ID}
            ref={filtersRef}
            $scrollbarWidth={scrollbarWidth}
            $width={width}
            $height={height}
          >
            {filtersContent}
          </ExpandCollapseFiltersWrapper>
        </div>
      </ExpandCollapse>
    );
  }

  return filtersContent;
};

export default FiltersColumn;

const NewLookHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
  padding: 2px 0 12px;
  background: ${(p) => p.theme.colors.cardBgColor};
  border-bottom: ${(p) => p.theme.borders.lightest};
  /* When stuck, also paint over the scroll container's top padding. */
  box-shadow: 0 -40px 0 0 ${(p) => p.theme.colors.cardBgColor};
`;
const NewLookTitle = styled.h3`
  flex: 1;
  margin: 0;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 16px;
  line-height: 24px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const NewLookReset = styled.button`
  height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.control}px;
  background: transparent;
  color: ${(p) => p.theme.colors.primaryBlue};
  font: inherit;
  font-size: 13px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  cursor: pointer;
  &:hover {
    background: ${(p) => p.theme.colors.lightBlue};
  }
`;
const NewLookClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.pill}px;
  background: transparent;
  color: ${(p) => p.theme.colors.secondaryText};
  cursor: pointer;
  &:hover {
    background: ${(p) => p.theme.colors.cardLightBgColor};
    color: ${(p) => p.theme.colors.titleColor};
  }
`;
const FiltersWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;
const ExpandCollapseFiltersWrapper = styled(Card)<{
  $scrollbarWidth: number;
  $height?: string;
  $width?: number;
}>`
  width: ${(p) => p.$width || 300}px;
  height: ${(p) => p.$height || "100%"};
  position: fixed;
  padding-bottom: 60px;
  padding-right: ${(p) => 16 - p.$scrollbarWidth}px;
  overflow-y: auto;
  &:not(:hover)::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
  &::-webkit-scrollbar {
    width: 8px !important;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* A real border: the card's inset ring would sit under the sticky
         header, which paints over it. */
      border: 1px solid ${p.theme.colors.cardBorderColor};
      box-shadow: ${p.theme.shadows.card};
      /* As tall as its sections, up to the available height. */
      height: auto;
      max-height: ${p.$height || "100%"};
      padding-bottom: 16px;
    `}
`;
const Sections = styled(Flex)``;
const NewLookEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 40px 16px 24px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  ${Sections}:not(:empty) + & {
    display: none;
  }
`;
const NewLookEmptyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-bottom: 8px;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.cardLightBgColor};
`;
const NewLookEmptyTitle = styled.div`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 14px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
`;
